/**
 * utcDay.ts — the SINGLE owner of UTC calendar-day parsing/formatting for the
 * incident/report surfaces (S4 design pass 2026-10-15, docs/audits/
 * s4-design-pass-2026-10-15.md §2A). A date value on these surfaces is either
 * a CALENDAR-VALID UTC day or '—' — never an invented normalized date, never
 * a local-timezone shift, never the 1970 epoch day, never a throw.
 *
 * BOUNDARY (owner decision D1): `./calendarDate.ts` is a SEPARATE contract —
 * LOCAL-day rendering/inputs for the JHA/DOT surfaces (it intentionally never
 * timezone-round-trips a calendar date). Do NOT merge the two modules:
 * incident/report surfaces use ONLY this module; JHA/DOT surfaces use ONLY
 * calendarDate.ts.
 */

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The ONE totality guard: a strict `YYYY-MM-DD` shape plus a UTC-component
 * round-trip — `new Date('2026-02-31T00:00:00.000Z')` silently normalizes to
 * March 3 instead of being NaN, so the parsed UTC components must equal the
 * input integers or the string is REJECTED (never normalized). Calendar-
 * invalid strings (2026-02-31, 2023-02-29, 2026-13-01) yield null.
 * Internal core: every other export in this module is defined in terms of it.
 */
export function parseUtcDayParts(s: string): { y: number; m: number; d: number } | null {
  if (!DAY_RE.test(s)) return null;
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  const parsed = new Date(`${s}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getUTCFullYear() === y && parsed.getUTCMonth() + 1 === m && parsed.getUTCDate() === d
    ? { y, m, d }
    : null;
}

/** Calendar-valid `YYYY-MM-DD` check (semantics unchanged from the page helper it replaces). */
export const isValidDateInput = (s: string): boolean => parseUtcDayParts(s) !== null;

/**
 * TOTAL formatter: the UTC calendar day `YYYY-MM-DD` for a valid date value,
 * '—' for null/undefined/''/unparseable, and — critically — '—' for a value
 * whose `YYYY-MM-DD` prefix is calendar-invalid (kills the normalization
 * trap: '2026-02-31' must never surface as '2026-03-03'). Used by BOTH the
 * table cell and the CSV export so the two consumers can never split.
 */
export function formatUtcDay(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  if (DAY_RE.test(v.slice(0, 10)) && parseUtcDayParts(v.slice(0, 10)) === null) return '—';
  return d.toISOString().slice(0, 10);
}

/**
 * Whole-day UTC bounds for a custom range: `T00:00:00.000Z` / `T23:59:59.999Z`
 * (matches the server's UTC-day filter semantics). Returns null unless BOTH
 * inputs are calendar-valid and `from <= to` — callers must not build a
 * request from a null result.
 */
export function utcDayBounds(from: string, to: string): { fromIso: string; toIso: string } | null {
  if (!isValidDateInput(from) || !isValidDateInput(to) || from > to) return null;
  return {
    fromIso: new Date(`${from}T00:00:00.000Z`).toISOString(),
    toIso: new Date(`${to}T23:59:59.999Z`).toISOString(),
  };
}
